// Shared label maps for bug reports / feature requests. Used by the reporter
// dialog ("My reports" tab) and the super-admin triage table so both surfaces
// stay in sync with the DB CHECK constraints in 50-bug-reports.sql.

export const BUG_REPORT_TYPES = ['bug', 'feature_request', 'support_request'] as const;
export type BugReportType = (typeof BUG_REPORT_TYPES)[number];

export const BUG_REPORT_STATUSES = [
  'new',
  'in_progress',
  'pending_release',
  'reopened',
  'planned',
  'future',
  'done',
  'declined',
] as const;
export type BugReportStatus = (typeof BUG_REPORT_STATUSES)[number];

export const BUG_REPORT_SEVERITIES = ['low', 'medium', 'high'] as const;
export type BugReportSeverity = (typeof BUG_REPORT_SEVERITIES)[number];

export const REPORT_TYPE_LABELS: Record<BugReportType, string> = {
  bug: 'Bug',
  feature_request: 'Feature request',
  support_request: 'Support request',
};

export const REPORT_SEVERITY_LABELS: Record<BugReportSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const REPORT_STATUS_LABELS: Record<BugReportStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  pending_release: 'Pending Release',
  reopened: 'Reopened',
  planned: 'Planned',
  future: 'Future',
  done: 'Done',
  declined: 'Declined',
};

/**
 * True when an attachment path points at a video, so the thread renders an
 * inline <video> player instead of an <img>. Detection is by extension (paths
 * are stored, not MIME); playback is intentionally broad (mp4/mov/m4v/webm)
 * even though upload currently accepts MP4 only, so any existing/future video
 * still plays. Shared by the reporter thread (my-reports-list) and the admin
 * triage thread (bug-reports-table).
 */
export function isVideoAttachmentPath(path: string): boolean {
  return /\.(mp4|mov|m4v|webm)$/i.test(path);
}

/**
 * True when an attachment path points at an image (→ <img> thumbnail). Anything
 * that is neither video nor image (pdf/doc/docx) renders as a download link.
 */
export function isImageAttachmentPath(path: string): boolean {
  return /\.(png|jpe?g|webp|gif)$/i.test(path);
}

// Single source of truth for bug-report attachment MIME types, shared by the
// submission dialog and the reply composer so their accept-lists can't drift.
// MUST mirror the bug_reports bucket's allowed_mime_types in 16-storage.sql.
// Word doc/docx match the customer_documents precedent (ALLOWED_MIME_TYPES in
// app/(tenant)/_lib/schema/allowed-mime-types.ts).
export const BUG_REPORT_ACCEPTED_MIME = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'video/mp4',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

// Derive the storage-path extension for an upload. Prefers the real filename
// extension (so .doc/.docx/.pdf are preserved), falling back to a MIME map when
// the name has none. Shared so the dialog and the reply composer stay in sync.
export function getAttachmentExtension(file: File): string {
  const match = /\.[a-z0-9]+$/i.exec(file.name);
  if (match) return match[0].toLowerCase();

  switch (file.type) {
    case 'image/png':
      return '.png';
    case 'image/jpeg':
    case 'image/jpg':
      return '.jpg';
    case 'image/webp':
      return '.webp';
    case 'video/mp4':
      return '.mp4';
    case 'application/pdf':
      return '.pdf';
    case 'application/msword':
      return '.doc';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return '.docx';
    default:
      return '';
  }
}
